import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useRef } from 'react';
import { AdminSpecialty } from '../types/types';
import { AvailableSpecialtiesLoader } from '../loaders/SpecialtiesLoader';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  styled,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { RoundedButton } from '../styles/CustomButtons';
import React from 'react';
import { useUIState } from '../context/uiState';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useSessionReplayCapture, useEventTracking } from '@services/statsig';
import { UTMTracker } from '@services/utm';
import {
  useUpdateOnboardingStep,
  useValidateReferralCodeMutation,
  useAddSpecialtyMutation,
  useUpdateUserInfoMutation,
  useUpdateUserReferralInfoMutation,
  useSendPhoneVerificationCodeMutation,
} from '@features/user';
import { TermsOfServiceModal } from './modals/ToSModal';
import { PhoneVerificationModal } from './modals/PhoneVerificationModal';
import { Link } from '@mui/material';

type UserFormType = {
  firstName: string;
  lastName: string;
  email: string;
  institution: string;
  specialization: string;
  discovery_channel: string;
  discovery_channel_text: string;
  referral_code: string;
  mobile_phone_num: string;
  checked_sms_consent: boolean;
  checked_sms_product_consent: boolean;
  accepted_tos: boolean;
};

interface UserInfoFormProps {
  onSuccessfulFormSubmission?: () => void;
}

interface IDiscoveryOption {
  label: string;
  value: string;
}

const CustomMuiTelInput = styled(MuiTelInput)`
  // Custom padding for the input
  & input {
    padding-left: 0px;
  }
`;

// always puts 'other' as last option
const shuffleArray = (array: IDiscoveryOption[]): IDiscoveryOption[] => {
  const otherOption = array.find(option => option.value === 'other');
  const filteredArray = array.filter(option => option.value !== 'other');

  for (let i = filteredArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [filteredArray[i], filteredArray[j]] = [filteredArray[j], filteredArray[i]]; // ES6 destructuring swap
  }

  if (otherOption) {
    filteredArray.push(otherOption);
  }

  return filteredArray;
};

export const UserInfoForm = ({ onSuccessfulFormSubmission }: UserInfoFormProps) => {
  const { onboarding: onboardingMetrics, phoneVerification, error: errorTracking } = useEventTracking();
  const referralCodeRef = localStorage.getItem('referralCode');
  const [formInfo, setFormInfo] = useState<UserFormType>({
    firstName: '',
    lastName: '',
    email: '',
    institution: '',
    specialization: '',
    discovery_channel: '',
    discovery_channel_text: '',
    referral_code: referralCodeRef ? referralCodeRef : '',
    mobile_phone_num: '',
    checked_sms_consent: false,
    checked_sms_product_consent: false,
    accepted_tos: false,
  });

  const updateOnboardingStep = useUpdateOnboardingStep();
  const { startCapture } = useSessionReplayCapture();

  // API mutation hooks
  const [validateReferralCodeMutation] = useValidateReferralCodeMutation();
  const [addSpecialtyMutation] = useAddSpecialtyMutation();
  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const [updateUserReferralInfoMutation] = useUpdateUserReferralInfoMutation();
  const [sendPhoneVerificationCodeMutation, { isLoading: isSendingCode }] =
    useSendPhoneVerificationCodeMutation();

  // Define the options for the "How did you find us?" dropdown
  const discoveryOptions = [
    { label: 'LinkedIn', value: 'linkedin_ad' },
    { label: 'Facebook / Instagram', value: 'facebook_ad' },
    { label: 'Google', value: 'google_ad' },
    {
      label: 'Friends / Colleagues',
      value: 'friend_colleague',
    },
    { label: 'Conference event', value: 'conference_event' },
    { label: 'Podcast', value: 'podcast' },
    { label: 'My employer', value: 'part_of_group' },
    { label: 'Newsletter', value: 'newsletter' },
    { label: 'Blog', value: 'blog' },
    { label: 'Someone on the JotPsych team called me', value: 'jotpsych_team_call' },
    { label: 'AI tool (ChatGPT, Claude, Perplexity, etc.)', value: 'ai_tool' },
    { label: 'Other', value: 'other' },
  ];

  const shuffledDiscoveryOptions = React.useMemo(() => shuffleArray([...discoveryOptions]), []);

  // would like to use these in future version, but need to change specialties on backend first
  // - 07/02/24
  // const specialtyOptions = [
  //   { label: "Psychiatry", value: "psychiatry" },
  //   { label: "Therapy", value: "therapy" },
  //   { label: "Psychology", value: "psychology" },
  //   { label: "Addiction Medicine", value: "addiction_medicine" },
  //   { label: "Palliative Care", value: "palliative" },
  //   { label: "Primary Care", value: "primary" },
  //   { label: "Integrated Medicine", value: "integrated" },
  //   { label: "Other", value: "other" },
  // ];

  const isOtherSelected = formInfo.discovery_channel === 'other';
  const [availableSpecialties, setAvailableSpecialties] = useState<AdminSpecialty[]>();
  const [selectedSpecialty, setSelectedSpecialty] = useState<AdminSpecialty>();
  const { user } = useAuth0();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [noEmailOnLoad, setNoEmailOnLoad] = useState(!user?.email);
  const [isLoading, setIsLoading] = useState(false);
  const { state } = useUIState();
  const { platform } = state;
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsProductConsent, setSmsProductConsent] = useState(false);
  const [isMobilePhoneValid, setIsMobilePhoneValid] = useState(false);
  const [showToSModal, setShowToSModal] = useState(false);

  // Phone verification state
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<{
    formInfo: UserFormType;
    referredByUserId: string;
  } | null>(null);

  // States for referral code validation
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [isReferralValid, setIsReferralValid] = useState<boolean | null>(null);
  const referralDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [formInputErrors, setFormInputErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    referral_code: '',
    discovery_channel: '',
    discovery_channel_text: '',
    specialty: '',
    mobile_phone_num: '',
    checked_sms_consent: '',
    checked_sms_product_consent: '',
    accepted_tos: '',
  });

  const fetchSpecialtiesData = async () => {
    const specialties = await AvailableSpecialtiesLoader();
    const visibleSpecialties = specialties.filter(specialty => !specialty.hidden);
    setAvailableSpecialties(visibleSpecialties);
  };

  const handleSelectSpecialty = (event: SelectChangeEvent) => {
    if (!availableSpecialties) {
      return;
    }
    const selectedSpecialty = availableSpecialties.find(
      specialty => specialty.specialty_id === event.target.value,
    );
    if (!selectedSpecialty) {
      return;
    }
    setSelectedSpecialty(selectedSpecialty);
    setFormInfo(prev => ({
      ...prev,
      specialization: selectedSpecialty.specialty_name,
    }));
  };

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    // Whitelist the BE bypass number (BYPASS_PHONE_NUMBER in phone_verification_service.py):
    // libphonenumber rejects all 555 numbers as reserved, but this one round-trips end-to-end
    // and support relies on it to unblock real users. Other 555 numbers stay invalid.
    const normalized = phoneNumber.replace(/\s/g, '');
    const withPrefix = normalized.startsWith('+') ? normalized : `+1${normalized}`;
    if (withPrefix === '+15551112233') {
      return true;
    }
    return matchIsValidTel(phoneNumber, {
      onlyCountries: [],
      excludedCountries: [],
      continents: [],
    });
  };

  const handlePhoneChange = (newValue: string) => {
    if (newValue.trim() === '') {
      // Don't clear error immediately - let blur or submit handle required validation
      setIsMobilePhoneValid(false);
      setSmsConsent(false);
      setSmsProductConsent(false);
      setFormInfo(prev => ({
        ...prev,
        mobile_phone_num: '',
        checked_sms_consent: false,
        checked_sms_product_consent: false,
      }));
    } else if (newValue.trim() === '+1') {
      setFormInfo(prev => ({ ...prev, mobile_phone_num: '' }));
      setIsMobilePhoneValid(false);
      // Don't clear error immediately - let blur or submit handle required validation
    } else {
      const isValid = validatePhoneNumber(newValue);
      setIsMobilePhoneValid(isValid);
      setFormInfo(prev => ({ ...prev, mobile_phone_num: newValue }));

      if (isValid) {
        setFormInputErrors(prev => ({ ...prev, mobile_phone_num: '' }));
      } else if (newValue.length > 1) {
        // Only show invalid format error if user has entered something meaningful
        setFormInputErrors(prev => ({
          ...prev,
          mobile_phone_num: 'The phone number you entered is invalid.',
        }));
      }
    }
  };

  // Handle debounced referral code validation
  const handleReferralCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value;
    setFormInfo(prev => ({ ...prev, referral_code: code }));

    // Clear any existing validation error
    setFormInputErrors(prev => ({ ...prev, referral_code: '' }));

    // Clear previous timer
    if (referralDebounceTimerRef.current) {
      clearTimeout(referralDebounceTimerRef.current);
    }

    // If empty, reset validation state
    if (!code.trim()) {
      setIsReferralValid(null);
      return;
    }

    // Set validating state
    setIsValidatingReferral(true);

    // Set a new timer
    referralDebounceTimerRef.current = setTimeout(async () => {
      try {
        const result = await validateReferralCode(code);
        setIsReferralValid(!!result);
        if (!result) {
          setFormInputErrors(prev => ({
            ...prev,
            referral_code: 'Invalid referral code.',
          }));
        }
      } catch (error) {
        setIsReferralValid(false);
        setFormInputErrors(prev => ({
          ...prev,
          referral_code: 'Error validating referral code.',
        }));
        errorTracking.track(
          error instanceof Error ? error : 'Referral code validation failed',
          'onboarding_referral_validation',
          { step: 'profile', action: 'validate_referral_code_debounced' },
        );
      } finally {
        setIsValidatingReferral(false);
      }
    }, 1000); // 1 second debounce
  };

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.target.value.trim() === '' ||
      formInfo.mobile_phone_num === '' ||
      formInfo.mobile_phone_num === '+1'
    ) {
      setFormInputErrors(prev => ({ ...prev, mobile_phone_num: 'Phone number is required.' }));
      setSmsConsent(false);
      setSmsProductConsent(false);
      setFormInfo(prev => ({
        ...prev,
        checked_sms_consent: false,
        checked_sms_product_consent: false,
      }));
    } else {
      const phone_number = formInfo.mobile_phone_num.startsWith('+')
        ? formInfo.mobile_phone_num
        : `+1${formInfo.mobile_phone_num}`;
      const isValid = validatePhoneNumber(phone_number);
      setIsMobilePhoneValid(isValid);
      setFormInfo(prev => ({ ...prev, mobile_phone_num: phone_number }));

      if (isValid) {
        setFormInputErrors(prev => ({ ...prev, mobile_phone_num: '' }));
      } else {
        setFormInputErrors(prev => ({
          ...prev,
          mobile_phone_num: 'The phone number you entered is invalid.',
        }));
      }
    }
  };

  const handleSmsConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSmsConsent(isChecked);

    // When checking marketing consent while both are unchecked, check both
    if (isChecked && !smsProductConsent) {
      setSmsProductConsent(true);
      setFormInfo(prev => ({
        ...prev,
        checked_sms_consent: isChecked,
        checked_sms_product_consent: true,
      }));
    } else {
      setFormInfo(prev => ({
        ...prev,
        checked_sms_consent: isChecked,
      }));
    }
  };

  const handleSmsProductConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSmsProductConsent(isChecked);

    // When checking product consent while both are unchecked, check both
    if (isChecked && !smsConsent) {
      setSmsConsent(true);
      setFormInfo(prev => ({
        ...prev,
        checked_sms_product_consent: isChecked,
        checked_sms_consent: true,
      }));
    } else {
      setFormInfo(prev => ({
        ...prev,
        checked_sms_product_consent: isChecked,
      }));
    }
  };

  const validateForm = async (formInfo: UserFormType, referredByCode: string) => {
    let errors = {
      firstName: '',
      lastName: '',
      email: '',
      referral_code: '',
      discovery_channel: '',
      discovery_channel_text: '',
      specialty: '',
      mobile_phone_num: '',
      checked_sms_consent: '',
      checked_sms_product_consent: '',
      accepted_tos: '',
    };

    const trimmedFirstName = formInfo.firstName.trim();
    const trimmedLastName = formInfo.lastName.trim();
    const emailAddress = formInfo.email;
    const discoveryChannel = formInfo.discovery_channel;
    const discoveryChannelText = formInfo.discovery_channel_text.trim();
    let mobilePhoneNumber = formInfo.mobile_phone_num;

    let referred_by_user_id: string | null = null;

    if (!trimmedFirstName) {
      errors.firstName = 'First name is required.';
    }
    if (!trimmedLastName) {
      errors.lastName = 'Last name is required.';
    }
    if (!emailAddress) {
      errors.email = 'Email is required.';
    }
    if (!discoveryChannel) {
      errors.discovery_channel = 'Required Field';
    }

    // Phone number is required
    if (!mobilePhoneNumber || mobilePhoneNumber === '+1' || mobilePhoneNumber.trim() === '') {
      errors.mobile_phone_num = 'Phone number is required.';
    } else {
      if (!validatePhoneNumber(mobilePhoneNumber)) {
        errors.mobile_phone_num = 'The phone number you entered is invalid.';
      }

      // Marketing consent is required since phone is required
      if (!formInfo.checked_sms_consent) {
        errors.checked_sms_consent = 'Please check the box to consent to receive text messages.';
      }
    }

    if (discoveryChannel === 'other') {
      if (!discoveryChannelText) {
        errors.discovery_channel_text = 'Required Field';
      } else if (discoveryChannelText.length > 50) {
        errors.discovery_channel_text = 'Please limit your response to 50 characters.';
      }
    }

    if (!selectedSpecialty) {
      errors.specialty = 'Please select a specialty.';
    }
    if (referredByCode && referredByCode.length != 0) {
      const validationResult = await validateReferralCode(referredByCode);
      if (!validationResult) {
        errors.referral_code = 'The referral code you entered is invalid.';
      } else {
        referred_by_user_id = validationResult;
      }
    }
    if (!formInfo.accepted_tos) {
      errors.accepted_tos = 'You must accept the Terms of Service to continue.';
    }

    return { errors, referred_by_user_id: referred_by_user_id || '' };
  };

  async function validateReferralCode(referredByCode: string): Promise<string | null> {
    try {
      const result = await validateReferralCodeMutation({
        referred_by_code: referredByCode,
      }).unwrap();
      if (result.status === 200 && result.referred_by_user_id) {
        return result.referred_by_user_id;
      }
      return null;
    } catch (error) {
      errorTracking.track(
        error instanceof Error ? error : 'Referral code validation failed at submission',
        'onboarding_referral_validation',
        { step: 'profile', action: 'validate_referral_code_submit' },
      );
      return null;
    }
  }

  async function updateSpecialty(specialtyId: string) {
    if (selectedSpecialty) {
      try {
        await addSpecialtyMutation({ specialty_id: specialtyId }).unwrap();
      } catch (error) {
        console.error('Failed to add specialty:', error);
        errorTracking.track(
          error instanceof Error ? error : 'Failed to add specialty',
          'onboarding_specialty_update',
          { step: 'profile', action: 'add_specialty' },
        );
      }
    }
  }

  async function updateUserInfo(submissionData: UserFormType & { name: string }) {
    const utmParams = getUTMParameters(); // Get UTM parameters
    const signupParams = getSignupParameters(); // Get all signup parameters

    const invite_token = localStorage.getItem('invite_token');
    try {
      const result = await updateUserInfoMutation({
        user_info: submissionData,
        platform: platform,
        utm: utmParams,
        signup_params: signupParams,
        invite_token: invite_token,
      }).unwrap();

      if (result.status === 200) {
        // Clean up all signup markers
        localStorage.removeItem('invite_token');
        localStorage.removeItem('urlParams');
        localStorage.removeItem('referralCode'); // Also remove if still present
        // Note: User info is now stored in RTK Query cache - it will be automatically updated via cache invalidation
        if (onSuccessfulFormSubmission) {
          onSuccessfulFormSubmission();
          clearUTMParameters();
        } else {
          const win: Window = window;
          win.location = `/`;
        }
        return {
          success: true,
          stripeCustomerId: result.stripe_customer_id,
          stripeSubscriptionId: result.stripe_subscription_id,
        };
      }
      return { success: false };
    } catch (error) {
      errorTracking.track(
        error instanceof Error ? error : 'Failed to save user profile',
        'onboarding_profile_save',
        { step: 'profile', action: 'update_user_info' },
      );
      return { success: false };
    }
  }

  function getUTMParameters(): Record<string, string> {
    // Use centralized UTM tracking service
    return UTMTracker.getUTMsForAPI();
  }

  function getSignupParameters(): Record<string, string> {
    // Get all parameters that were saved during signup
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams) return {};

    try {
      return Object.fromEntries(urlParams.entries());
    } catch (error) {
      console.error('Error parsing signup parameters:', error);
      return {};
    }
  }

  function clearUTMParameters(): void {
    // UTM parameters are automatically cleaned from URL by UTMTracker
    // This function is kept for backward compatibility
    UTMTracker.clear();
  }

  async function updateUserReferralInfo(
    referredByCode: string,
    referredByUserId: string,
    stripeCustomerId: string,
    stripeSubscriptionId: string,
  ) {
    try {
      await updateUserReferralInfoMutation({
        is_new_user: true,
        referred_by_code: referredByCode,
        referred_by_user_rewarded: false,
        referred_by_user_id: referredByUserId,
        stripe_customer_id: stripeCustomerId,
        stripe_subscription_id: stripeSubscriptionId,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update referral info:', error);
      errorTracking.track(
        error instanceof Error ? error : 'Failed to update referral info',
        'onboarding_referral_update',
        { step: 'profile', action: 'update_referral_info' },
      );
    }
  }

  const handleSubmit = async () => {
    setIsLoading(true);

    const { errors, referred_by_user_id } = await validateForm(formInfo, formInfo.referral_code);
    if (referralCodeRef) {
      localStorage.removeItem('referralCode');
    }
    setFormInputErrors(errors);

    if (
      errors.firstName ||
      errors.lastName ||
      errors.email ||
      errors.referral_code ||
      errors.discovery_channel ||
      errors.discovery_channel_text ||
      errors.mobile_phone_num ||
      errors.checked_sms_consent ||
      errors.checked_sms_product_consent ||
      errors.specialty ||
      errors.accepted_tos
    ) {
      setIsLoading(false);
      return;
    }

    // Skip phone verification for invited users — they were explicitly invited
    // by an existing user/org, so the trust signal already exists.
    const inviteToken = localStorage.getItem('invite_token');
    if (inviteToken) {
      setPendingFormData({ formInfo, referredByUserId: referred_by_user_id });
      await handlePhoneVerified({ formInfo, referredByUserId: referred_by_user_id });
      return;
    }

    // Send phone verification code
    try {
      const result = await sendPhoneVerificationCodeMutation({
        sendVerificationCodeRequest: {
          phone_number: formInfo.mobile_phone_num,
          email: formInfo.email,
        },
      }).unwrap();

      if (result.success && result.code) {
        phoneVerification.requested({ source: 'onboarding' });
        setVerificationCode(result.code);
        // Check if BE returned demo flag to auto-fill code
        setIsDemoMode('demo' in result && Boolean(result.demo));
        setPendingFormData({ formInfo, referredByUserId: referred_by_user_id });
        setShowPhoneVerificationModal(true);
      } else {
        setFormInputErrors(prev => ({
          ...prev,
          mobile_phone_num: result.message || 'Failed to send verification code. Please try again.',
        }));
      }
    } catch (error) {
      setFormInputErrors(prev => ({
        ...prev,
        mobile_phone_num: 'Failed to send verification code. Please try again.',
      }));
      errorTracking.track(
        error instanceof Error ? error : 'Failed to send phone verification code',
        'onboarding_phone_verification',
        { step: 'profile', action: 'send_verification_code' },
      );
    }

    setIsLoading(false);
  };

  const handlePhoneVerified = async (
    directFormData?: { formInfo: UserFormType; referredByUserId: string },
  ) => {
    const data = directFormData || pendingFormData;
    if (!data) return;

    setShowPhoneVerificationModal(false);
    setIsLoading(true);

    const { formInfo: verifiedFormInfo, referredByUserId } = data;

    const fullName = `${verifiedFormInfo.firstName.trim()} ${verifiedFormInfo.lastName.trim()}`;

    const submissionData = {
      ...verifiedFormInfo,
      name: fullName,
    };

    if (selectedSpecialty) {
      await updateSpecialty(selectedSpecialty.specialty_id);
    }

    const userInfoResult = await updateUserInfo(submissionData);

    if (userInfoResult.success) {
      // Track profile completion event
      onboardingMetrics.profileCompleted({
        specialty: selectedSpecialty?.specialty_name,
        discovery_channel: verifiedFormInfo.discovery_channel,
        discovery_channel_text:
          verifiedFormInfo.discovery_channel === 'other'
            ? verifiedFormInfo.discovery_channel_text
            : undefined,
        has_referral_code: !!verifiedFormInfo.referral_code,
        has_phone_number: !!verifiedFormInfo.mobile_phone_num,
        sms_consent: verifiedFormInfo.checked_sms_consent,
        sms_product_consent: verifiedFormInfo.checked_sms_product_consent,
      });

      // Update ToS acceptance in onboarding steps
      if (verifiedFormInfo.accepted_tos) {
        await updateOnboardingStep('accepted_tos_0524', true);
      }

      const stripeCustomerId = userInfoResult.stripeCustomerId;
      const stripeSubscriptionId = userInfoResult.stripeSubscriptionId;
      // Call updateUserReferralInfo for all users with subscriptions to initialize them in the referral system.
      // This allows users to generate their own referral codes to share with others, regardless of whether
      // they were referred by someone else. Pass empty strings for referral fields if not provided.
      if (stripeCustomerId && stripeSubscriptionId) {
        await updateUserReferralInfo(
          verifiedFormInfo.referral_code || '',
          referredByUserId || '',
          stripeCustomerId,
          stripeSubscriptionId,
        );
      }
    }

    setPendingFormData(null);
    setIsLoading(false);
  };

  const handleResendCode = async () => {
    const result = await sendPhoneVerificationCodeMutation({
      sendVerificationCodeRequest: {
        phone_number: formInfo.mobile_phone_num,
        email: formInfo.email,
      },
    }).unwrap();

    if (result.success && result.code) {
      setVerificationCode(result.code);
    } else {
      throw new Error(result.message || 'Failed to resend code');
    }
  };

  useEffect(() => {
    // Start session replay capture when the component mounts
    startCapture('onboarding_flow');
    fetchSpecialtiesData();
  }, []);

  useEffect(() => {
    if (user) {
      if (user?.email) {
        setFormInfo(prev => ({ ...prev, email: user.email! }));
        setNoEmailOnLoad(false);
      } else {
        setNoEmailOnLoad(true);
      }
    }
  }, [user]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (referralDebounceTimerRef.current) {
        clearTimeout(referralDebounceTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <Stack sx={{ width: '100%' }} spacing={3}>
        <Typography
          variant="h4"
          gutterBottom
          component="div"
          fontWeight={700}
          align="center"
          sx={{ mb: 4 }}
        >
          Get Started
        </Typography>
        <TextField
          fullWidth
          id="firstName"
          label="First Name"
          variant="outlined"
          margin="dense"
          size="small"
          required
          error={!!formInputErrors.firstName}
          helperText={formInputErrors.firstName}
          onChange={e => setFormInfo(prev => ({ ...prev, firstName: e.target.value }))}
        />
        <TextField
          fullWidth
          id="lastName"
          label="Last Name"
          variant="outlined"
          margin="dense"
          size="small"
          required
          error={!!formInputErrors.lastName}
          helperText={formInputErrors.lastName}
          onChange={e => setFormInfo(prev => ({ ...prev, lastName: e.target.value }))}
        />
        <CustomMuiTelInput
          forceCallingCode
          defaultCountry="US"
          placeholder="Mobile Phone Number"
          value={formInfo.mobile_phone_num}
          error={!!formInputErrors.mobile_phone_num}
          helperText={formInputErrors.mobile_phone_num}
          size="small"
          required
          onChange={handlePhoneChange}
          onBlur={handlePhoneBlur}
        />
        {noEmailOnLoad && (
          <TextField
            fullWidth
            id="email"
            label="Email Address"
            variant="outlined"
            margin="dense"
            size="small"
            required
            error={!!formInputErrors.email}
            helperText={formInputErrors.email}
            value={formInfo.email}
            onChange={e => setFormInfo(prev => ({ ...prev, email: e.target.value }))}
          />
        )}
        {availableSpecialties && (
          <FormControl fullWidth margin="dense" size="small" error={!!formInputErrors.specialty}>
            <InputLabel id="specialty-label">Specialty</InputLabel>
            <Select
              labelId="specialty-label"
              id="specialty"
              value={selectedSpecialty?.specialty_id || ''}
              onChange={handleSelectSpecialty}
              label="Specialty"
              size="small"
            >
              {availableSpecialties.map(specialty => (
                <MenuItem key={specialty.specialty_id} value={specialty.specialty_id}>
                  {specialty.specialty_name}
                </MenuItem>
              ))}
            </Select>
            {formInputErrors.specialty && (
              <FormHelperText>{formInputErrors.specialty}</FormHelperText>
            )}
          </FormControl>
        )}
        <FormControl
          fullWidth
          margin="dense"
          size="small"
          error={!!formInputErrors.discovery_channel}
        >
          <InputLabel id="discovery-source-label">How did you find us?</InputLabel>
          <Select
            labelId="discovery-source-label"
            id="discoverySource"
            value={formInfo.discovery_channel || ''}
            onChange={e => setFormInfo(prev => ({ ...prev, discovery_channel: e.target.value }))}
            label="How did you find us?"
            size="small"
            required
          >
            <MenuItem value="">Select an option...</MenuItem>
            {shuffledDiscoveryOptions.map((option: IDiscoveryOption) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {formInputErrors.discovery_channel && (
            <FormHelperText>{formInputErrors.discovery_channel}</FormHelperText>
          )}
        </FormControl>
        {isOtherSelected && (
          <TextField
            fullWidth
            id="otherReferral"
            label="Please share how you found us"
            variant="outlined"
            margin="dense"
            size="small"
            error={!!formInputErrors.discovery_channel_text}
            helperText={formInputErrors.discovery_channel_text}
            onChange={e =>
              setFormInfo(prev => ({ ...prev, discovery_channel_text: e.target.value }))
            }
          />
        )}
        <TextField
          fullWidth
          id="referralCode"
          label="Referral Code (Optional)"
          variant="outlined"
          margin="dense"
          size="small"
          error={!!formInputErrors.referral_code}
          helperText={formInputErrors.referral_code}
          onChange={handleReferralCodeChange}
          value={formInfo.referral_code}
          InputProps={{
            endAdornment: formInfo.referral_code && (
              <InputAdornment position="end">
                {isValidatingReferral ? (
                  <CircularProgress size={20} />
                ) : isReferralValid === true ? (
                  <CheckCircleIcon color="success" />
                ) : isReferralValid === false ? (
                  <CancelIcon color="error" />
                ) : null}
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mt: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            SMS Consent
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" error={!!formInputErrors.checked_sms_consent}>
                {formInputErrors.checked_sms_consent && (
                  <FormHelperText sx={{ mb: 1 }}>
                    {formInputErrors.checked_sms_consent}
                  </FormHelperText>
                )}
                <FormControlLabel
                  control={<Checkbox checked={smsConsent} onChange={handleSmsConsentChange} />}
                  label={
                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                      I consent to receive promotional and marketing text messages from JotPsych,
                      including offers and product updates. Message frequency may vary. Msg & data
                      rates may apply. Reply STOP to unsubscribe or HELP for help. See our{' '}
                      <Link
                        href="https://www.jotpsych.com/terms-of-use-1"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                        }}
                      >
                        SMS Terms
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="https://www.jotpsych.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                        }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  }
                  sx={{
                    alignItems: 'start',
                    m: 0,
                  }}
                  labelPlacement="end"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                error={!!formInputErrors.checked_sms_product_consent}
              >
                {formInputErrors.checked_sms_product_consent && (
                  <FormHelperText sx={{ mb: 1 }}>
                    {formInputErrors.checked_sms_product_consent}
                  </FormHelperText>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={smsProductConsent}
                      onChange={handleSmsProductConsentChange}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                      I consent to receive service-related text messages from JotPsych, including
                      documentation reminders, workflow guidance, and responses to questions I send.
                      Message frequency varies. Msg & data rates may apply. Reply STOP to
                      unsubscribe or HELP for help. See our{' '}
                      <Link
                        href="https://www.jotpsych.com/terms-of-use-1"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                        }}
                      >
                        SMS Terms
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="https://www.jotpsych.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                        }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  }
                  sx={{
                    alignItems: 'start',
                    m: 0,
                  }}
                  labelPlacement="end"
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <FormControl fullWidth margin="dense" size="small" error={!!formInputErrors.accepted_tos}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formInfo.accepted_tos}
                onChange={e => setFormInfo(prev => ({ ...prev, accepted_tos: e.target.checked }))}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the{' '}
                <Link
                  component="span"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowToSModal(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: 'primary.main',
                  }}
                >
                  Terms of Service
                </Link>
              </Typography>
            }
            sx={{
              alignItems: 'center',
              m: 0,
            }}
          />
          {formInputErrors.accepted_tos && (
            <FormHelperText>{formInputErrors.accepted_tos}</FormHelperText>
          )}
        </FormControl>
        <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <RoundedButton
            variant="contained"
            color="primary"
            sx={{ px: 4, py: 1.5 }}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={'24px'} />
            ) : (
              <Typography variant="body1" fontWeight={700}>
                Next
              </Typography>
            )}
          </RoundedButton>
        </Stack>
      </Stack>
      <TermsOfServiceModal
        isOpen={showToSModal}
        onClose={() => setShowToSModal(false)}
        isReadOnly={true}
      />
      <PhoneVerificationModal
        isOpen={showPhoneVerificationModal}
        onClose={() => {
          setShowPhoneVerificationModal(false);
          setPendingFormData(null);
        }}
        onVerified={handlePhoneVerified}
        phoneNumber={formInfo.mobile_phone_num}
        expectedCode={verificationCode}
        onResendCode={handleResendCode}
        isResending={isSendingCode}
        autoFillCode={isDemoMode}
      />
    </>
  );
};
